'use client';

import { useCallback, useEffect, useRef } from 'react';

import { useCommentsContext } from '@/components/Contexts/CommentsContext';

import type {
  ProxyCommentMarkedReadMessage,
  ProxyMarkCommentReadFailedMessage,
  ProxyMarkCommentReadMessage,
} from '@/types/feedback';

// How long a pin stays blocked from re-sending. The proxy answers with either
// FB_COMMENT_MARKED_READ or FB_MARK_COMMENT_READ_FAILED, but if that reply never
// arrives (iframe reloaded, message dropped) this lets a later selection retry.
const MARK_READ_TIMEOUT_MS = 10_000;

// A comment is New until the owner reads it, and the partner view is the owner
// view, so only it marks comments read. The write goes through the owner proxy,
// the same path resolve and reopen take.
export function useMarkCommentRead(
  feedbackId: string,
  metaSiteId: string,
  isPartner: boolean,
) {
  const {
    state: { pins },
    dispatch,
  } = useCommentsContext();
  const pendingPinIdsRef = useRef(new Set<string>());
  const pendingTimeoutsRef = useRef(new Map<string, number>());

  const clearPending = useCallback((pinId: string) => {
    const timeoutId = pendingTimeoutsRef.current.get(pinId);
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
      pendingTimeoutsRef.current.delete(pinId);
    }
    pendingPinIdsRef.current.delete(pinId);
  }, []);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.source !== window) return;

      if (event.data?.type === 'FB_COMMENT_MARKED_READ') {
        const { pinId, revision, status } = (
          event.data as ProxyCommentMarkedReadMessage
        ).payload;
        clearPending(pinId);
        dispatch({
          type: 'UPDATE_PIN_STATUS',
          payload: { id: pinId, status: status ?? 'OPEN', revision },
        });
        return;
      }

      if (event.data?.type === 'FB_MARK_COMMENT_READ_FAILED') {
        // Stay on New with no toast so a later selection can retry.
        const { pinId } = (event.data as ProxyMarkCommentReadFailedMessage)
          .payload;
        clearPending(pinId);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [clearPending, dispatch]);

  const markCommentRead = useCallback(
    (pinId: string) => {
      if (!isPartner) return;

      const pin = pins.find((candidate) => candidate.id === pinId);
      if (pin?.status !== 'NEW' || !pin.commentId) return;
      if (pendingPinIdsRef.current.has(pinId)) return;

      pendingPinIdsRef.current.add(pinId);
      pendingTimeoutsRef.current.set(
        pinId,
        window.setTimeout(() => clearPending(pinId), MARK_READ_TIMEOUT_MS),
      );
      const message: ProxyMarkCommentReadMessage = {
        type: 'FB_MARK_COMMENT_READ',
        payload: {
          pinId,
          commentId: pin.commentId,
          revision: pin.revision,
          feedbackId,
          metaSiteId,
        },
      };
      window.postMessage(message, window.location.origin);
    },
    [clearPending, feedbackId, isPartner, metaSiteId, pins],
  );

  return { markCommentRead };
}
